<template>
  <page :name="$t('Conferma offerta')" :title="title" :state="[offerState, companyState]">
    <template #breadcrumbs>
      <pf-breadcrumb-item :to="{ name: 'offers' }">{{ $t('Offerte') }}</pf-breadcrumb-item>
    </template>

    <pf-page-section v-if="company && offer?.id">
      <payment-report
        :result="reportQueryData.result"
        :error="reportQueryData.error"
        route="offers"
        :success="$t('Offerta confermata')"
        :link="$t('Torna alle offerte')"
        v-if="company"
      >
        <pf-card v-if="askBilling" class="pf-v6-u-mb-md">
          <pf-card-header>
            <pf-content>
              <pf-title h="3">
                {{ $t('Dati di fatturazione') }}
                <template v-if="company.hasCompleteBillingData">
                  <pf-icon status="success">
                    <ok-icon />
                  </pf-icon>
                </template>
              </pf-title>
              <p v-if="company.hasCompleteBillingData">
                {{ $t("Dati di fatturazione inseriti correttamente.")}}
              </p>
              <p v-else>
                {{ $t("Prima di procedere con la conferma dell'ordine abbiamo bisogno di tutti i dati di fatturazione.")}}
              </p>
            </pf-content>
          </pf-card-header>

          <pf-card-body v-if="!company.hasCompleteBillingData">
            <billing-form
              embed-for="order"
              @submit="refresh"
            />
          </pf-card-body>
        </pf-card>

        <x-form
          @submit="submit"
          :prevent-submit="!canOrder"
          :submit-text="submitText"
          :submit-help="!offer.enable_orders ? $t('Non hai i permessi per confermare l\'offerta. Richiedili ad un utente amministratore dell\'azienda intestataria.') : undefined"
          ouia-id="confirm-offer-form"
        >
          <pf-card class="pf-v6-u-mb-xl">
            <pf-card-header>
              <pf-title h="3">{{ $t("Riepilogo dell'offerta") }}</pf-title>
            </pf-card-header>

            <pf-card-body>
              <offer-detail :offerta="offer" />
              <input type="hidden" name="idOfferta" :value="offer.id">
            </pf-card-body>
          </pf-card>

          <template v-if="canOrder">
            <payment-cashback
              v-if="login.id_azienda === offer.id_azienda"
              v-model="cashback"
              :totale-offerta-ivato="offer.gran_totale_ivato"
              :currency="offer.currency"
            />
            <form-group :name="$t('Metodo di pagamento')">
              {{ paymentCustomLabel }}
            </form-group>
          </template>
        </x-form>
      </payment-report>
    </pf-page-section>

    <pf-alert v-else variant="danger" :title="$t('Errore: Offerta non trovata')" />
  </page>
</template>

<script lang="ts" setup>
import { ref, computed, provide } from 'vue';
import { until, invoke } from '@vueuse/core';

import BillingForm from '../components/billing-form.vue';
import OfferDetail from '../components/offer-detail.vue';
import PaymentReport from '../components/payment-report.vue';
import PaymentCashback from '@/components/payment-cashback.vue';
import { asyncComputed } from '@common/asyncComputed';
import { $t } from '../i18n';
import { type Company, CompanyResource, CompanyInjectionKey, OffersResource } from '@/resources';
import { pop } from '@common/utils';
import OkIcon from '@vue-patternfly/icons/ok-icon';
import { useRoute, useRouter } from 'vue-router';
import { useLoginStore } from '@/store/login';

const props = defineProps<{
  id: string,
}>();

const login = useLoginStore();
const router = useRouter();
const cashback = ref(0);
const askBilling = ref(false);

const { state: offerState, result: offer } = asyncComputed(async() => await new OffersResource().get(props.id));

const { state: companyState, result: company } = asyncComputed(
  async function() {
    await until(() => offerState.updating || offerState.pending).toBe(false);
    if (offerState.error || !offer.value?.id) {
      return null
    }
    return await new CompanyResource().get(offer.value.id_azienda);
  },
  null,
);

invoke(async() => {
  await until(() => company.value?.id).toBeTruthy();
  askBilling.value = !company.value?.hasCompleteBillingData;
});

provide(CompanyInjectionKey, company.value ?? {} as Company);

const canOrder = computed(() => company.value?.hasCompleteBillingData !== false && offer.value.stato !== 'ordine' && offer.value.enable_orders);
const submitText = computed(() => {
  if (offer.value.stato === 'ordine') {
    return $t('Ordine già confermato');
  }
  return $t('Conferma offerta');
});

const route = useRoute();

const reportQueryData = computed(() => {
  const result = pop(route.query.res);
  return {
    result: result ? Number(result) : undefined,
    error: pop(route.query.err) ?? undefined,
  };
});

async function refresh() {
  await Promise.all([
    offerState.update(),
    companyState.update(),
  ]);
}

async function submit() {
  if (!offer.value?.id) {
    return;
  }

  await new OffersResource().put(offer.value.id, {
    stato: 'confermata',
    cashback_amount: cashback.value > 0 ? cashback.value : undefined,
  });
  router.push({ name: 'order_result', params: { id: offer.value.id }, query: { res: 0 } });
}

const title = computed(() => {
  if (!offer.value) {
    return;
  }
  return $t("Conferma dell'offerta: {0}", [offer.value.titolo_offerta]);
});

const paymentCustomLabel = computed(() =>  {
  if (offer.value?.free_period) {
    return offer.value.testing_mode === 'rinnova' ?
      $t('GRATIS. Se non disdici prima ti arriverà fattura tra {0} giorni', [offer.value.free_period]) :
      $t('GRATIS per {0} giorni. Se non ci chiederai il rinnovo i servizi verranno sospesi e cancellati tra {0} giorni', [offer.value.free_period]);
  }

  if (!company.value?.fatturazione_mensile) {
    return company.value?.pagamento;
  }

  return `${$t(company.value?.pagamento)} - ${$t('incluso in fatturazione mensile')}`;
});
</script>
